<script setup lang="ts">
  // Vue core imports
  import { ref } from 'vue'

  import { useI18n } from 'vue-i18n'

  // External library imports

  import { Lock01Icon } from '@gohighlevel/ghl-icons/24/outline'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'
  import CPForm from '@ui/components/CPForm.vue'
  import CPFormItem from '@ui/components/CPFormItem.vue'
  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../../core-layer/utils'
  import { useAuth } from '../../composables/useAuth'
  import { useCaptchaChallenge } from '../../composables/useCaptchaChallenge'
  import { LoginEvents } from '../../types/auth'
  import { getErrorMessage, validateForm } from '../../utils'
  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Type imports

  // Props definitions
  const props = defineProps({
    passwordResetToken: {
      type: String,
      required: true,
    },
    contactId: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      default: '',
    },
    /**
     * When true (clientportal /login with defer), persist session via saveAuthAfterLogin after API returns a token.
     * False on `/auth` flow — parent handles saveAuthAfterLogin to avoid duplicate persistence.
     */
    persistSessionAfterSetPassword: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
  }>()

  const { t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()
  const loading = ref(false)
  const setPasswordDisabled = ref(false)
  const { showRecaptcha, reCaptchaToken, onVerify, onError, onExpired, handleRateLimit } = useCaptchaChallenge(setPasswordDisabled)
  const newPasswordError = ref('')
  const confirmPasswordError = ref('')

  const rules = {
    newPassword: {
      required: true,
      min: 8,
      validator: (_rule: any, value: string) => {
        newPasswordError.value = ''

        if (!value) {
          newPasswordError.value = t('auth.validation.passwordRequired')
          return false
        }

        if (value.length < 8) {
          newPasswordError.value = t('auth.validation.passwordMinLength')
          return false
        }

        return true
      },
      trigger: ['input', 'blur'],
    },
    confirmPassword: {
      required: true,
      validator: (_rule: any, value: string) => {
        confirmPasswordError.value = ''

        if (!value) {
          confirmPasswordError.value = t('auth.validation.confirmPasswordRequired')
          return false
        }

        if (value !== formValue.value.newPassword) {
          confirmPasswordError.value = t('auth.validation.passwordsDoNotMatch')
          return false
        }

        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  const formValue = ref({
    newPassword: '',
    confirmPassword: '',
  })

  const formRef = ref()

  // User-defined functions
  const handleValidateClick = async () => {
    const isValidated = await validateForm(formRef.value)
    if (!isValidated) {
      return false
    }
    // Defensive check to ensure values exist before trimming
    if (!formValue.value?.newPassword || !formValue.value?.confirmPassword) {
      console.error('Password values are undefined after validation')
      return false
    }

    try {
      loading.value = true
      errorMessage.value = null

      const auth = useAuth()
      const { data } = await auth.setPassword({
        newPassword: formValue.value.newPassword.trim(),
        confirmPassword: formValue.value.confirmPassword.trim(),
        passwordResetToken: props.passwordResetToken,
        contactId: props.contactId,
        captchaToken: reCaptchaToken.value,
      })
      // Persist the cookie via the finalize helper. It prefers a fresh token from
      // the set-password response; if that is absent it falls back to the original
      // deferred-login data stashed in the auth store. This covers both backend
      // shapes (full AuthResponse vs. `{ success: true }` only).
      let loginCallbackPayload = data
      if (props.persistSessionAfterSetPassword) {
        const finalized = await auth.finalizeDeferredAuthAfterPasswordSet(data ?? null)
        if (finalized === null) {
          errorMessage.value = t('auth.setPassword.sessionFinalizeFailed')
          loading.value = false
          return
        }
        loginCallbackPayload = finalized.data ?? data
      }
      emit('loginCallback', {
        eventType: LoginEvents.LOGIN_SUCCESSFUL,
        ...loginCallbackPayload,
      })
      loading.value = false
    } catch (error: any) {
      if (!handleRateLimit(error)) {
        errorMessage.value = getErrorMessage(error)
      }
      loading.value = false
    }
  }
</script>

<template>
  <CPForm
    :id="createElementId('set-password', 'modal', 'form')"
    ref="formRef"
    class="flex w-full flex-col gap-4 items-center"
    :model="formValue"
    :rules="rules"
    @keyup.enter="handleValidateClick"
  >
    <!--
      Highrise form-item feedback defaults to a single-line clamp; long hints overlap the next label.
      Turn off built-in feedback and render hint/error in normal flow below the control.
    -->
    <div class="w-full">
      <CPFormItem class="w-full" :label="t('auth.setPassword.newPasswordLabel')" required path="newPassword" :show-feedback="false">
        <CPInput
          :id="createElementId('set-password', 'modal', 'new-password-input')"
          v-model="formValue.newPassword"
          size="lg"
          type="password"
          :placeholder="t('auth.setPassword.newPasswordPlaceholder')"
          show-password-on="click"
        >
          <template #prefix>
            <Lock01Icon class="mr-2 h-5 w-5 pr-1"></Lock01Icon>
          </template>
        </CPInput>
      </CPFormItem>
      <div class="mt-2 w-full min-w-0">
        <CPText
          size="sm"
          class="block whitespace-normal break-words leading-snug"
          :class="newPasswordError ? 'text-danger' : 'text-clientportal-font-secondary'"
        >
          {{ newPasswordError || t('auth.messages.createStrongPassword') }}
        </CPText>
      </div>
    </div>

    <div class="w-full">
      <CPFormItem class="w-full" :label="t('auth.setPassword.confirmPasswordLabel')" required path="confirmPassword" :show-feedback="false">
        <CPInput
          :id="createElementId('set-password', 'modal', 'confirm-password-input')"
          v-model="formValue.confirmPassword"
          size="lg"
          type="password"
          :placeholder="t('auth.setPassword.confirmPasswordPlaceholder')"
          show-password-on="click"
        >
          <template #prefix>
            <Lock01Icon class="mr-2 h-5 w-5 pr-1"></Lock01Icon>
          </template>
        </CPInput>
      </CPFormItem>
      <div class="mt-2 w-full min-w-0">
        <CPText
          size="sm"
          class="block whitespace-normal break-words leading-snug"
          :class="confirmPasswordError ? 'text-danger' : 'text-clientportal-font-secondary'"
        >
          {{ confirmPasswordError || t('auth.messages.confirmNewPassword') }}
        </CPText>
      </div>
    </div>

    <div v-if="errorMessage" class="text-danger mb-4 w-full">
      <CPText size="sm">{{ errorMessage }}</CPText>
    </div>

    <Recaptcha
      v-if="showRecaptcha"
      :id="createElementId('set-password', 'modal', 'recaptcha')"
      class="flex w-full justify-center"
      @expired="onExpired"
      @verify="onVerify"
      @error="onError"
    />

    <CPButton
      :id="createElementId('set-password', 'modal', 'submit-button')"
      class="mt-4 w-full rounded-xl shadow-sm"
      type="primary"
      size="lg"
      :loading="loading"
      :disabled="loading || setPasswordDisabled"
      @click="handleValidateClick"
    >
      {{ t('auth.setPassword.button') }}
    </CPButton>
  </CPForm>
</template>
